import { CloseButton } from "@ui/CloseButton";
import {
	createEffect,
	createUniqueId,
	type JSX,
	onCleanup,
	type ParentComponent,
	Show,
} from "solid-js";
import { Portal } from "solid-js/web";
import "./Modal.css";
import { VStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";

const FOCUSABLE_SELECTOR =
	'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusableElements(container: HTMLElement): HTMLElement[] {
	const nodes = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
	return Array.from(nodes).filter((el) => {
		if (el.getAttribute("aria-hidden") === "true") return false;
		return el.offsetParent !== null;
	});
}

/** Ref-count so nested modals don’t unlock scroll prematurely */
let bodyScrollLockDepth = 0;
let lockedScrollY = 0;
let savedBodyPosition = "";
let savedBodyTop = "";
let savedBodyLeft = "";
let savedBodyRight = "";
let savedBodyWidth = "";

function acquireBodyScrollLock() {
	if (bodyScrollLockDepth === 0) {
		lockedScrollY =
			window.scrollY ||
			window.pageYOffset ||
			document.documentElement.scrollTop;
		const body = document.body;
		savedBodyPosition = body.style.position;
		savedBodyTop = body.style.top;
		savedBodyLeft = body.style.left;
		savedBodyRight = body.style.right;
		savedBodyWidth = body.style.width;

		body.style.position = "fixed";
		body.style.top = `-${lockedScrollY}px`;
		body.style.left = "0";
		body.style.right = "0";
		body.style.width = "100%";
	}
	bodyScrollLockDepth++;
}

function releaseBodyScrollLock() {
	bodyScrollLockDepth = Math.max(0, bodyScrollLockDepth - 1);
	if (bodyScrollLockDepth === 0) {
		const body = document.body;
		body.style.position = savedBodyPosition;
		body.style.top = savedBodyTop;
		body.style.left = savedBodyLeft;
		body.style.right = savedBodyRight;
		body.style.width = savedBodyWidth;
		window.scrollTo({ top: lockedScrollY, left: 0, behavior: "instant" });
	}
}

export type ModalProps = {
	open: () => boolean;
	onClose: () => void;
	/** Pre-title text to display above the title. */
	preTitle?: string;
	/** Visible heading; sets `aria-labelledby`. Prefer this or `ariaLabel`. */
	title?: JSX.Element;
	/** When there is no visible title, sets `aria-label` on the dialog. */
	ariaLabel?: string;
	/** Extra class on the dialog panel (e.g. flush media layouts). */
	class?: string;
	/** Default true. */
	closeOnBackdropClick?: boolean;
	/** Default true. */
	showCloseButton?: boolean;
	/** Dialog max width. Default `medium` (36rem). `small` is 32.5rem, `large` is 48rem. */
	size?: "small" | "medium" | "large";
};

/**
 * Centered modal dialog: focus trap, Escape and backdrop dismiss, scroll lock,
 * `role="dialog"` + `aria-modal="true"`.
 *
 * [Design System →](http://localhost:5174/component/Modal)
 */
export const Modal: ParentComponent<ModalProps> = (props) => {
	const uid = createUniqueId();
	const rootId = `gvtui-modal-${uid}`;
	const titleId = `${rootId}-title`;

	let dialogRef: HTMLElement | null = null;
	let previousActiveElement: HTMLElement | null = null;

	createEffect(() => {
		if (!props.open()) {
			return;
		}
		previousActiveElement = document.activeElement as HTMLElement | null;
		queueMicrotask(() => {
			const el = dialogRef;
			if (!el) return;
			const focusable = getFocusableElements(el);
			if (focusable.length > 0) {
				focusable[0].focus();
			} else {
				el.focus();
			}
		});
	});

	createEffect(() => {
		if (!props.open()) {
			previousActiveElement?.focus?.();
			return;
		}
		acquireBodyScrollLock();
		onCleanup(() => releaseBodyScrollLock());
	});

	createEffect(() => {
		if (!props.open()) return;
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.preventDefault();
				e.stopPropagation();
				props.onClose();
				return;
			}
			if (e.key !== "Tab") return;
			const panel = dialogRef;
			if (!panel) return;
			const focusable = getFocusableElements(panel);
			if (focusable.length === 0) return;
			const current = document.activeElement as HTMLElement | null;
			const currentIndex = current ? focusable.indexOf(current) : -1;
			if (e.shiftKey) {
				if (currentIndex <= 0 || !panel.contains(current)) {
					e.preventDefault();
					focusable[focusable.length - 1].focus();
				}
			} else {
				if (currentIndex === focusable.length - 1 || !panel.contains(current)) {
					e.preventDefault();
					focusable[0].focus();
				}
			}
		};
		window.addEventListener("keydown", onKeyDown, true);
		onCleanup(() => window.removeEventListener("keydown", onKeyDown, true));
	});

	const labelledBy = () => (props.title !== undefined ? titleId : undefined);
	const labelOnly = () =>
		props.title === undefined ? (props.ariaLabel ?? "Dialog") : undefined;

	return (
		<Show when={props.open()}>
			<Portal mount={document.body}>
				{/** biome-ignore lint/a11y/noStaticElementInteractions: scrim closes modal; Escape also closes */}
				<div
					class="gvtui-modal-backdrop"
					role="presentation"
					onClick={(e) => {
						if (props.closeOnBackdropClick === false) return;
						if (e.target === e.currentTarget) {
							e.preventDefault();
							e.stopPropagation();
							props.onClose();
						}
					}}
				>
					<div
						ref={(el) => {
							dialogRef = el;
						}}
						id={rootId}
						class={`gvtui-modal ${props.class ?? ""}`}
						classList={{
							[`gvtui-modal--${props.size ?? "medium"}`]: true,
						}}
						role="dialog"
						aria-modal="true"
						aria-labelledby={labelledBy()}
						aria-label={labelOnly()}
						tabIndex={-1}
						onPointerDown={(e) => e.stopPropagation()}
					>
						<Show
							when={
								props.title !== undefined || props.showCloseButton !== false
							}
						>
							<div
								class="gvtui-modal__header"
								classList={{
									"gvtui-modal__header--close-only": props.title === undefined,
								}}
							>
								<VStack spacing="small">
									<Show when={props.preTitle !== undefined}>
										<Text
											family="mono"
											size="xsmall"
											color="fg3"
											weight="semi-bold"
											transform="uppercase"
										>
											{props.preTitle}
										</Text>
									</Show>
									<Show when={props.title !== undefined}>
										<h2 class="gvtui-modal__title" id={titleId}>
											{props.title}
										</h2>
									</Show>
								</VStack>
								<Show when={props.showCloseButton !== false}>
									<CloseButton
										aria-label="Close dialog"
										onClick={() => props.onClose()}
									/>
								</Show>
							</div>
						</Show>
						<div class="gvtui-modal__body">{props.children}</div>
					</div>
				</div>
			</Portal>
		</Show>
	);
};
